'use client';

import { LoadingSpinnerIcon } from '@/assets/icons/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { ErrorModal } from '@/components/ui/ErrorModal';
import type {
  FormSubmissionPayload,
  FormSubmissionResponse,
} from '@/lib/form-handlers';
import { devLog } from '@/lib/logging';
import { cn } from '@/lib/ui/cn';
import { pushFormEventToDataLayer } from '@/utils/dataLayer';
import { extractErrorMessage } from '@/utils/http-error-messages';
import { TrackingHelper } from '@/utils/tracking';
import EmbedFlow from '@formsort/react-embed';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

type FormState = 'idle' | 'submitting' | 'redirecting' | 'error';

export interface FormSortFormProps {
  /** FormSort flow label */
  flow: string;
  /** FormSort variant label */
  variant: string;
  /** Handler name to use for submission */
  handler: string;
  /** URL to redirect to after successful submission */
  thankYouPagePath?: string;
  /** Optional CTA text - if provided, shows button instead of form initially */
  drawerCta?: string;
  /** Optional anchor ID for the form container */
  anchorId?: string;
  /** Additional query params to pass to FormSort */
  queryParams?: [string, string][];
  /** Callback when form submission succeeds */
  onSuccess?: () => void;
  /** Callback when form submission fails */
  onError?: (error: unknown) => void;
  /** Additional className for container */
  className?: string;
}

function FormContainer({
  children,
  anchorId,
}: {
  children: React.ReactNode;
  anchorId?: string;
}) {
  return (
    <div id={anchorId} className='w-full'>
      {children}
    </div>
  );
}

export function FormSortForm({
  flow,
  variant,
  handler,
  thankYouPagePath = '/forms/thank-you',
  drawerCta,
  anchorId,
  queryParams = [],
  onSuccess,
  onError,
  className,
}: FormSortFormProps) {
  const [formState, setFormState] = useState<FormState>('idle');
  const [showForm, setShowForm] = useState(!drawerCta);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastFormData, setLastFormData] = useState<Record<
    string,
    unknown
  > | null>(null);

  const router = useRouter();
  const t = useTranslations('ContactUsPage');
  const tHttp = useTranslations('HttpErrors');

  // Get FormSort client from environment variable
  const client = process.env.NEXT_PUBLIC_FORMSORT_CLIENT;

  if (!client) {
    throw new Error(
      'NEXT_PUBLIC_FORMSORT_CLIENT environment variable must be set.'
    );
  }

  if (!flow || !variant) {
    throw new Error(
      'flow and variant props are required for FormSortForm component.'
    );
  }

  if (!handler) {
    throw new Error('handler prop is required for FormSortForm component.');
  }

  // Handle redirect after successful submission
  useEffect(() => {
    if (formState === 'redirecting') {
      const timer = setTimeout(() => {
        const redirectUrl = window.location.origin + thankYouPagePath;
        router.push(redirectUrl);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [formState, thankYouPagePath, router]);

  const handleShowForm = () => {
    setShowForm(true);
  };

  const closeErrorModal = () => {
    setFormState('idle');
    setSubmitError(null);
    if (anchorId) {
      setTimeout(() => {
        document
          .getElementById(anchorId)
          ?.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  };

  const handleFlowFinalized = async (data: {
    answers?: Record<string, unknown>;
  }) => {
    devLog('Form finalized:', data);
    setFormState('submitting');
    setSubmitError(null);

    try {
      const formData = data?.answers || {};
      setLastFormData(formData);

      const trackingData = TrackingHelper.getTrackingData();

      // Push analytics event
      try {
        pushFormEventToDataLayer({
          eventName: 'FlowFinalized',
          form_type: handler as 'contact_us' | 'sign_up',
          data: { ...formData, ...trackingData },
        });
      } catch (error) {
        console.error('Error sending data to dataLayer:', error);
      }

      const payload: FormSubmissionPayload = {
        handler,
        formData,
        trackingData,
      };

      const response = await fetch('/api/submit-formsort-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result: FormSubmissionResponse = await response.json();
        devLog('Form submitted successfully:', result);
        onSuccess?.();
        setFormState('redirecting');
      } else {
        const errorMessage = await extractErrorMessage(
          response,
          (key: string) => tHttp(key.replace('HttpErrors.', '')),
          t('error.defaultMessage')
        );

        setSubmitError(errorMessage);
        setFormState('error');
        onError?.(response);
      }
    } catch (error) {
      console.error('Unexpected error submitting form:', error);
      setSubmitError(t('error.unexpectedError'));
      setFormState('error');
      onError?.(error);
    }
  };

  // Loading state
  if (formState === 'submitting' || formState === 'redirecting') {
    const message =
      formState === 'redirecting' ? t('redirecting') : t('submitting');

    return (
      <div className='inset-0 z-50 flex items-center justify-center'>
        <div className='flex flex-col items-center space-y-4'>
          <LoadingSpinnerIcon className='text-sh-sky size-16' />
          <p className='text-sh-dark-gray'>{message}</p>
        </div>
      </div>
    );
  }

  // Drawer CTA mode
  if (drawerCta && !showForm) {
    return (
      <FormContainer anchorId={anchorId}>
        <div className={cn('bg-sh-paper flex justify-center py-8', className)}>
          <Button variant='dark' onClick={handleShowForm}>
            {drawerCta}
          </Button>
        </div>
      </FormContainer>
    );
  }

  // Form display
  return (
    <FormContainer anchorId={anchorId}>
      {(formState === 'idle' || formState === 'error') && (
        <div className={cn('formsort formsort__container', className)}>
          <EmbedFlow
            clientLabel={client}
            flowLabel={flow}
            variantLabel={variant}
            queryParams={[
              ...queryParams,
              // Repopulate form with last data if there was an error
              ...Object.entries(lastFormData ?? {}).map(
                ([key, value]) => [key, String(value)] as [string, string]
              ),
            ]}
            embedConfig={{
              autoHeight: true,
              style: {
                height: '100%',
                width: '100%',
              },
            }}
            onFlowLoaded={() => devLog('Form loaded')}
            onFlowClosed={() => devLog('Form closed')}
            onFlowFinalized={handleFlowFinalized}
            onStepLoaded={step => devLog('Step loaded:', step)}
            onStepCompleted={step => devLog('Step completed:', step)}
            onRedirect={() => ({ cancel: true })}
            onUnauthorized={() => devLog('Form unauthorized')}
          />
        </div>
      )}

      <ErrorModal
        isOpen={formState === 'error'}
        onClose={closeErrorModal}
        error={submitError || ''}
        title={t('error.title')}
      />
    </FormContainer>
  );
}
